// Admin › OTA Releases › Changelog — the API's shape, the data hook and the
// copy formats. The API (GET /api/admin/ota/changelog) does the work; see
// vortex-api/src/lib/otaChangelog.ts.

import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../services/api';

export type SectionKind = 'features' | 'fixes' | 'speedups' | 'reverts' | 'breaking' | 'other';

/** Who made a commit, and when it reached main. */
export interface CommitAuthor {
  name: string;
  at: string;
}

export interface ChangeItem {
  text: string;
  sha: string | null;
  url: string | null;
  author?: CommitAuthor | null;
  /** The help register rows it touches (Doc IDs, or "new <MODULE>"), from its commit's Help-guide line. */
  docs?: string[];
}

/** One row of the help register, by Doc ID. */
export interface RegisterRow {
  element: string;
  /** "Home → Top Toolbar → Alarm" */
  path: string;
  module: string;
  /** When it left the Scout ("2.49.27: …"); set for a retired row. */
  retired?: string;
}

export interface ChangeSection {
  kind: SectionKind;
  title: string;
  items: ChangeItem[];
}

export interface ChangelogRelease {
  version: string;
  tag: string;
  url: string;
  publishedAt: string | null;
  sentToScouts: boolean;
  sections: ChangeSection[];
  note: string | null;
  /** Not on Scouts yet: 'released' (tagged, waiting for the deploy) or 'proposed' (in an open release PR). */
  stage?: 'released' | 'proposed';
}

export interface NoteLine {
  kind: 'bullet' | 'text';
  lead: string | null;
  text: string;
}

export interface ChangelogAppUpdate {
  app: string;
  package: string;
  name: string;
  fromVersion: string | null;
  toVersion: string;
  sentVersions: string[];
  live: boolean;
  releases: ChangelogRelease[];
  missingRelease: boolean;
  customerNotes: NoteLine[] | null;
  customerNotesRaw: string | null;
  notesSameAs: string | null;
  compareUrl: string | null;
}

export interface ChangelogOta {
  firstOfferedAt: string;
  apps: ChangelogAppUpdate[];
}

export interface UnreportedApp {
  app: string;
  package: string;
  name: string;
  liveVersion: string;
  release: ChangelogRelease | null;
}

export interface LiveApp {
  app: string;
  package: string;
  name: string;
  version: string;
  /** When Scouts were first offered this version; null when Scouts never report the app. */
  since: string | null;
}

/** One app with changes Scouts don't have yet, shaped like an update so the page draws it the same way. */
export interface PendingApp extends ChangelogAppUpdate {
  stage: 'released' | 'proposed';
  pr: { number: number; url: string; draft: boolean } | null;
}

export interface OtaChangelogResponse {
  since: string;
  github: 'ok' | 'not_configured' | 'error';
  githubError?: string;
  otas: ChangelogOta[];
  unreported: UnreportedApp[];
  liveApps: LiveApp[];
  /** Apps with changes Scouts don't have yet. */
  pending: PendingApp[];
  /** The app with the most commits on main in the last 72 hours, if any. */
  busiest?: string | null;
  /** Whether open release PRs could be read ('no_access' until the GitHub app has Pull requests: read). */
  prAccess: 'ok' | 'no_access' | 'unavailable';
  /** The help register rows the changes name, by Doc ID. */
  register?: Record<string, RegisterRow>;
}

export function useOtaChangelog() {
  return useQuery<OtaChangelogResponse>({
    queryKey: ['ota', 'changelog'],
    queryFn: () => api.adminGetOtaChangelog(),
    staleTime: 60_000,
  });
}

// ---- New Zealand dates ----

const NZ = 'Pacific/Auckland';

function parts(iso: string, opts: Intl.DateTimeFormatOptions): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-NZ', { timeZone: NZ, ...opts }).formatToParts(new Date(iso))) out[p.type] = p.value;
  return out;
}

/** "Thu 15 Oct 2026" */
export function nzDay(iso: string): string {
  const p = parts(iso, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  return `${p.weekday} ${p.day} ${p.month} ${p.year}`;
}

/** "Thu 15 Oct" */
export function nzShortDay(iso: string): string {
  const p = parts(iso, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${p.weekday} ${p.day} ${p.month}`;
}

/** "October 2026" */
export function nzMonth(iso: string): string {
  const p = parts(iso, { month: 'long', year: 'numeric' });
  return `${p.month} ${p.year}`;
}

/**
 * A stable, readable id for an update, for the page address and links:
 * its New Zealand date and time, e.g. "2026-10-15-1248".
 */
export function updateId(iso: string): string {
  const p = parts(iso, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
  return `${p.year}-${p.month}-${p.day}-${p.hour}${p.minute}`;
}

/** "just now", "3 hours ago", "4 days ago", "2 weeks ago", "3 months ago". */
export function ago(iso: string, now = Date.now()): string {
  const mins = Math.max(0, Math.round((now - Date.parse(iso)) / 60_000));
  const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;
  if (mins < 1) return 'just now';
  if (mins < 60) return plural(mins, 'minute');
  const hours = Math.round(mins / 60);
  if (hours < 24) return plural(hours, 'hour');
  const days = Math.round(hours / 24);
  if (days < 14) return plural(days, 'day');
  if (days < 56) return plural(Math.round(days / 7), 'week');
  return plural(Math.round(days / 30), 'month');
}

/** "12:48 pm" */
export function nzTime(iso: string): string {
  const p = parts(iso, { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${p.hour}:${p.minute} ${(p.dayPeriod ?? '').toLowerCase()}`;
}

/** "30 Sept 2026": the New Zealand day, for "since …". */
export function nzSince(iso: string): string {
  return new Intl.DateTimeFormat('en-NZ', { timeZone: NZ, day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

/** "NZDT" or "NZST" */
export function nzZone(iso: string): string {
  return parts(iso, { timeZoneName: 'short' }).timeZoneName ?? 'NZT';
}

// ---- summaries ----

const KIND_WORDS: Record<SectionKind, [string, string]> = {
  features: ['feature', 'features'],
  fixes: ['fix', 'fixes'],
  speedups: ['speed-up', 'speed-ups'],
  reverts: ['revert', 'reverts'],
  breaking: ['breaking change', 'breaking changes'],
  other: ['change', 'changes'],
};
const KIND_ORDER: SectionKind[] = ['breaking', 'features', 'fixes', 'speedups', 'reverts', 'other'];

export interface KindCount {
  kind: SectionKind;
  count: number;
  /** "1 fix", "6 fixes" */
  label: string;
}

function countKinds(releases: ChangelogRelease[]): Map<SectionKind, number> {
  const counts = new Map<SectionKind, number>();
  for (const r of releases) for (const s of r.sections) counts.set(s.kind, (counts.get(s.kind) ?? 0) + s.items.length);
  return counts;
}

function toKindCounts(counts: Map<SectionKind, number>): KindCount[] {
  return KIND_ORDER.filter((k) => counts.get(k)).map((k) => {
    const n = counts.get(k)!;
    return { kind: k, count: n, label: `${n} ${KIND_WORDS[k][n === 1 ? 0 : 1]}` };
  });
}

/** Kind counts for a list of kinds, one per change, in the fixed order. */
export function kindCountsOf(kinds: SectionKind[]): KindCount[] {
  const counts = new Map<SectionKind, number>();
  for (const k of kinds) counts.set(k, (counts.get(k) ?? 0) + 1);
  return toKindCounts(counts);
}

/** Each kind of change in one app's part of an update, in a fixed order. */
export function appKindCounts(a: ChangelogAppUpdate): KindCount[] {
  return toKindCounts(countKinds(a.releases));
}

/** The same across every app in an update (or only those listed). */
export function updateKindCounts(apps: ChangelogAppUpdate[]): KindCount[] {
  return toKindCounts(countKinds(apps.flatMap((a) => a.releases)));
}

/** Kind counts for one release. */
export function releaseKindCounts(r: ChangelogRelease): KindCount[] {
  return toKindCounts(countKinds([r]));
}

/** "2 features · 1 fix · 1 speed-up" across every release in the update. */
/** An app's knowledge base changes, across its releases, each once. */
export interface KbChange {
  key: string;
  text: string;
  kind: SectionKind;
  docs: string[];
  sha: string | null;
  url: string | null;
  author: CommitAuthor | null;
  pr?: { number: number; url: string } | null;
}

export function kbChangesOf(a: ChangelogAppUpdate): KbChange[] {
  const seen = new Set<string>();
  const out: KbChange[] = [];
  for (const r of a.releases) {
    for (const s of r.sections) {
      for (const it of s.items) {
        const key = it.sha ?? `${r.tag}:${it.text}`;
        if (!it.docs?.length || seen.has(key)) continue;
        seen.add(key);
        out.push({ key, text: it.text, kind: s.kind, docs: it.docs, sha: it.sha, url: it.url, author: it.author ?? null });
      }
    }
  }
  return out;
}

export function changeSummary(a: ChangelogAppUpdate): string {
  return appKindCounts(a).map((k) => k.label).join(' · ');
}

/** "PolarisOS and VortexNav", "PolarisOS, VortexNav and TrueHelm" ("or" for "No updates to …") */
export function appNames(apps: { name: string }[], joiner: 'and' | 'or' = 'and'): string {
  const names = apps.map((a) => a.name);
  return names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} ${joiner} ${names[names.length - 1]}`;
}

/** Updates grouped by New Zealand month, keeping their order. */
export function byMonth<T extends { firstOfferedAt: string }>(otas: T[]): { label: string; otas: T[] }[] {
  const out: { label: string; otas: T[] }[] = [];
  for (const o of otas) {
    const label = nzMonth(o.firstOfferedAt);
    const last = out[out.length - 1];
    if (last && last.label === label) last.otas.push(o);
    else out.push({ label, otas: [o] });
  }
  return out;
}

// ---- search ----

export interface TextPiece {
  text: string;
  hit: boolean;
}

/** Split text around every case-insensitive match of q. */
export function highlight(text: string, q: string): TextPiece[] {
  const needle = q.trim().toLowerCase();
  if (!needle) return [{ text, hit: false }];
  const out: TextPiece[] = [];
  const lower = text.toLowerCase();
  let at = 0;
  for (let i = lower.indexOf(needle); i !== -1; i = lower.indexOf(needle, i + needle.length)) {
    if (i > at) out.push({ text: text.slice(at, i), hit: false });
    out.push({ text: text.slice(i, i + needle.length), hit: true });
    at = i + needle.length;
  }
  if (at < text.length) out.push({ text: text.slice(at), hit: false });
  return out;
}

export interface SearchHit {
  /** 'note' for a line Scouts were shown. */
  kind: SectionKind | 'note';
  text: string;
  sha: string | null;
  url: string | null;
  author?: CommitAuthor | null;
  /** Found by its commit id, so the page marks the commit. */
  bySha?: true;
}

/** What the search box asks for: text, and a commit id, PR number or version when it looks like one. */
export interface SearchQuery {
  text: string;
  /** The start of a commit id (4 to 40 hex characters), from the query or a pasted commit link. */
  sha: string | null;
  /** "#858", "858" or a pasted PR link. */
  pr: number | null;
  /** "3.92.1" or "v3.92.1". */
  version: string | null;
}

export function parseSearch(raw: string): SearchQuery {
  const q = raw.trim();
  const sha = /\/commit\/([0-9a-f]{4,40})/i.exec(q)?.[1] ?? (/^[0-9a-f]{4,40}$/i.test(q) ? q : null);
  const pr = /\/pull\/(\d+)/.exec(q)?.[1] ?? /^#?(\d{2,6})$/.exec(q)?.[1] ?? null;
  const version = /^v?(\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?)$/i.exec(q)?.[1] ?? null;
  return { text: q.toLowerCase(), sha: sha?.toLowerCase() ?? null, pr: pr ? Number(pr) : null, version };
}

/** Whether a commit (its short id, and its GitHub link carrying the full one) starts with the searched id. */
export function shaMatches(q: SearchQuery, sha: string | null, url: string | null): boolean {
  if (!q.sha) return false;
  const full = /\/commit\/([0-9a-f]+)/i.exec(url ?? '')?.[1] ?? sha ?? '';
  return full.toLowerCase().startsWith(q.sha) || (sha ?? '').toLowerCase().startsWith(q.sha);
}

export interface SearchGroup {
  /** The update's id, or PENDING_ID for something not on Scouts yet. */
  updateId: string;
  /** Empty for something not on Scouts yet. */
  firstOfferedAt: string;
  app: string;
  name: string;
  version: string;
  sentToScouts: boolean;
  hits: SearchHit[];
}

/** Searches start at two characters, so one letter doesn't match everything. */
export const SEARCH_MIN = 2;

/** The id search results and the page address use for "Not sent yet". */
export const PENDING_ID = 'pending';

/**
 * Every change line and customer note matching q, not-on-Scouts-yet first: by
 * text, by commit id, or, for a version or a release PR's number, the whole
 * release with what Scouts were told.
 */
export function searchChanges(otas: ChangelogOta[], q: string, pending: PendingApp[] = []): SearchGroup[] {
  const query = parseSearch(q);
  const needle = query.text;
  if (needle.length < SEARCH_MIN) return [];
  const out: SearchGroup[] = [];
  const groups: { id: string; at: string; apps: ChangelogAppUpdate[] }[] = [
    ...(pending.length ? [{ id: PENDING_ID, at: '', apps: pending }] : []),
    ...otas.map((o) => ({ id: updateId(o.firstOfferedAt), at: o.firstOfferedAt, apps: o.apps })),
  ];
  for (const o of groups) {
    const id = o.id;
    for (const a of o.apps) {
      const prNumber = 'pr' in a ? (a as PendingApp).pr?.number ?? null : null;
      const whole = (r: ChangelogRelease) => r.version === query.version || (query.pr !== null && r.stage === 'proposed' && prNumber === query.pr);
      // An unsent release whose notes are still the live version's has no message of its own to find.
      const noteLines = repeatsOldNotes(a) ? [] : (a.customerNotes ?? []).map((n) => [n.lead, n.text].filter(Boolean).join(' '));
      a.releases.forEach((r, i) => {
        const hits: SearchHit[] = r.sections.flatMap((s) => s.items.flatMap((it): SearchHit[] => {
          const bySha = shaMatches(query, it.sha, it.url);
          if (!bySha && !whole(r) && !it.text.toLowerCase().includes(needle)) return [];
          return [{ kind: s.kind, text: it.text, sha: it.sha, url: it.url, author: it.author, ...(bySha ? { bySha: true as const } : {}) }];
        }));
        // Customer notes belong to the version Scouts got, the newest release.
        const notes: SearchHit[] = i === 0
          ? noteLines.filter((t) => whole(r) || t.toLowerCase().includes(needle)).map((t) => ({ kind: 'note' as const, text: t, sha: null, url: null }))
          : [];
        const all = [...notes, ...hits];
        if (all.length) out.push({ updateId: id, firstOfferedAt: o.at, app: a.app, name: a.name, version: r.version, sentToScouts: r.sentToScouts, hits: all });
      });
      const lonely = noteLines.filter((t) => a.toVersion === query.version || t.toLowerCase().includes(needle));
      if (!a.releases.length && lonely.length) {
        out.push({ updateId: id, firstOfferedAt: o.at, app: a.app, name: a.name, version: a.toVersion, sentToScouts: id !== PENDING_ID, hits: lonely.map((t) => ({ kind: 'note' as const, text: t, sha: null, url: null })) });
      }
    }
  }
  return out;
}

// ---- page address ----

/**
 * What is open, kept in the page address so a reload, the back button or a
 * shared link lands in the same place:
 *   open=<id>,<id>      updates open (absent: the newest; empty: none)
 *   apps=<id>.<app>,…   app sections open (absent: the first app of the newest update)
 *   app=<app>,<app>     only these apps' updates (absent: every app)
 *   q=<text>            search
 *   view=pending        "Not sent yet" instead of the sent updates (its apps are pending.<app>)
 */
export interface ViewState {
  open: Set<string>;
  apps: Set<string>;
  /** The apps chosen in "On Scouts now"; empty shows every app. */
  only: Set<string>;
  q: string;
  /** Which list: sent updates, or what is not on Scouts yet (view=pending). */
  tab: 'sent' | 'pending';
}

const list = (v: string | null) => new Set((v ?? '').split(',').map((x) => x.trim()).filter(Boolean));

export function readView(params: URLSearchParams, newest: ChangelogOta | undefined): ViewState {
  return readViewFor(params, newest ? updateId(newest.firstOfferedAt) : null, newest?.apps[0]?.app ?? null);
}

/** readView for any list: its newest entry's id and that entry's first app or part, opened when the address says nothing. */
export function readViewFor(params: URLSearchParams, newestId: string | null, firstApp: string | null): ViewState {
  const open = params.has('open') ? list(params.get('open')) : new Set(newestId ? [newestId] : []);
  const apps = params.has('apps')
    ? list(params.get('apps'))
    : new Set(newestId && open.has(newestId) && firstApp ? [`${newestId}.${firstApp}`] : []);
  return { open, apps, only: list(params.get('app')), q: params.get('q') ?? '', tab: params.get('view') === 'pending' ? 'pending' : 'sent' };
}

export function writeView(v: ViewState): URLSearchParams {
  const p = new URLSearchParams();
  p.set('open', [...v.open].join(','));
  p.set('apps', [...v.apps].join(','));
  if (v.only.size) p.set('app', [...v.only].join(','));
  if (v.q) p.set('q', v.q);
  if (v.tab === 'pending') p.set('view', 'pending');
  return p;
}

export function versionLine(a: ChangelogAppUpdate): string {
  return a.fromVersion ? `${a.name} ${a.fromVersion} → ${a.toVersion}` : `${a.name} ${a.toVersion}`;
}

// ---- copy formats ----

function releaseHeading(r: ChangelogRelease): string {
  const status = r.stage === 'proposed' ? 'in a release PR' : r.stage === 'released' ? 'released, not sent yet' : r.sentToScouts ? 'sent to Scouts' : 'not sent on its own';
  return `${r.version}, ${status}`;
}

/** Not on Scouts yet: a pending app carries the stage it is at. */
const isPending = (a: ChangelogAppUpdate): a is PendingApp => 'stage' in a && !!(a as PendingApp).stage;

/**
 * On its way with RELEASE_NOTES.md unchanged since the live version: Scouts
 * would be shown that version's message again, so it is not this release's.
 */
export const repeatsOldNotes = (a: ChangelogAppUpdate): boolean => isPending(a) && !!a.notesSameAs;

/** One app's part of an update, as plain text. */
export function appText(a: ChangelogAppUpdate): string {
  const lines: string[] = [versionLine(a), '', isPending(a) ? 'What Scouts will be shown:' : 'What Scouts were told:'];
  if (repeatsOldNotes(a)) {
    lines.push(`No message yet (Scouts would show ${a.notesSameAs}'s until one is written)`);
  } else if (a.customerNotes?.length) {
    for (const n of a.customerNotes) {
      const body = [n.lead, n.text].filter(Boolean).join(' ');
      lines.push(n.kind === 'bullet' ? `- ${body}` : body);
    }
  } else {
    lines.push('(no notes found)');
  }
  lines.push('', 'What changed:');
  if (!a.releases.length) {
    lines.push(a.missingRelease ? `(no release on GitHub for ${a.toVersion})` : '(no changes listed)');
  }
  const several = a.releases.length > 1;
  a.releases.forEach((r, i) => {
    if (several) {
      if (i > 0) lines.push('');
      lines.push(releaseHeading(r));
    }
    if (r.note) lines.push(r.note);
    for (const s of r.sections) {
      lines.push(s.title);
      for (const it of s.items) lines.push(`- ${it.text}${it.sha ? ` (${it.sha})` : ''}`);
    }
  });
  return lines.join('\n');
}

function otaHeading(o: ChangelogOta): string {
  return `Update sent to Scouts: ${nzDay(o.firstOfferedAt)}, first offered ${nzTime(o.firstOfferedAt)} ${nzZone(o.firstOfferedAt)}`;
}

export function otaText(o: ChangelogOta): string {
  return [otaHeading(o), ...o.apps.map((a) => `\n${appText(a)}`)].join('\n');
}

/** One app's part of an update, as Markdown. */
export function appMarkdown(a: ChangelogAppUpdate, level = 3): string {
  const h = '#'.repeat(level);
  const lines: string[] = [`${h} ${versionLine(a)}`, '', isPending(a) ? '**What Scouts will be shown**' : '**What Scouts were told**', ''];
  lines.push(repeatsOldNotes(a) ? `_No message yet (Scouts would show ${a.notesSameAs}'s until one is written)._` : (a.customerNotesRaw ?? '_No notes found._'));
  lines.push('', '**What changed**', '');
  if (!a.releases.length) {
    lines.push(a.missingRelease ? `_No release on GitHub for ${a.toVersion}._` : '_No changes listed._');
  }
  const several = a.releases.length > 1;
  for (const r of a.releases) {
    if (several) lines.push(`${h}# [${releaseHeading(r)}](${r.url})`, '');
    if (r.note) lines.push(r.note, '');
    for (const s of r.sections) {
      lines.push(`_${s.title}_`, '');
      for (const it of s.items) lines.push(`- ${it.text}${it.sha && it.url ? ` ([${it.sha}](${it.url}))` : ''}`);
      lines.push('');
    }
  }
  if (a.compareUrl) lines.push(`[All code changes](${a.compareUrl})`);
  return lines.join('\n').trimEnd();
}

export function otaMarkdown(o: ChangelogOta): string {
  return [`## ${otaHeading(o)}`, ...o.apps.map((a) => `\n${appMarkdown(a)}`)].join('\n');
}
